import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import ModalWrapper from 'Common/Components/ModalWrapper';
import { Dropdown } from 'Common/Components/Inputs';
import { useDispatch, useSelector } from 'react-redux';
import { selectTemplates } from 'store/ManageEditCreate/selectors';
import { getTemplates } from 'Screens/Contextualization/ManageEditCreate/actions/Actions';
import { applyTemplate, applyTemplateFromAsset, applyTemplateFromOutsideWs } from 'Screens/Contextualization/KnowledgeBase/actions/Actions';
import { closeModal } from 'store/modals/actions';
import { MODAL_APPLY_TEMPLATE } from 'store/modals/modals';
import { inputDropdownStyle } from 'Common/Components/Inputs/styled';
import {
  ModalContent, Title, Subtitle, ButtonsContainer, CancelButton, Fields
} from './styled';
import ExternalId from './components/ExternalId';
import Policies from './components/Policies';
import ImpactsTab from './components/ImpactsTab';
import Resolution from './components/Resolution';
import Description from './components/Description';
import Severity from './components/Severity';
import Data from './components/Data';
import { isEmpty } from 'lodash';
import CustomAttributes from './components/CustomAttributes';
import CVE from './components/CVE';
import { ThemeContext } from 'Context';
import Button from 'shared/ui/Button';

const getValidCvss = (template) => {
  if (template?.cvss4?.vector_string) return template.cvss4.vector_string;
  if (template?.cvss4?.base_score || template?.cvss4?.base_score === 0) return template.cvss4.base_score;
  if (template?.cvss3?.vector_string) return template.cvss3.vector_string;
  if (template?.cvss3?.base_score || template?.cvss3?.base_score === 0) return template.cvss3.base_score;
  if (template?.cvss2?.vector_string) return template.cvss2.vector_string;
  if (template?.cvss2?.base_score || template?.cvss2?.base_score === 0) return template.cvss2.base_score;
  return null;
};

// Whether each field actually has data in the template — used to decide if its
// block should render at all. Kept separate from selectedFields (the checkbox
// state), so unchecking a field only unchecks it instead of removing the block.
const getFieldsPresence = (template) => ({
  name: !!template.name,
  exploitation: !!template.exploitation,
  desc: !!template.desc,
  resolution: !!template.resolution,
  easeofresolution: !!template.easeofresolution,
  policyviolations: !!template.policyviolations?.length,
  impact: !!template.impact?.accountability || !!template.impact?.availability || !!template.impact?.confidentiality || !!template.impact?.integrity,
  data: !!template.data,
  external_id: !!template.external_id,
  refs: !!template.refs?.length,
  cve: !!template.cve?.length,
  cvss: getValidCvss(template) !== null,
  customfields: !isEmpty(template.customfields)
});

const ApplyTemplateModal = ({ entity, vulnsSelected }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const savedTemplates = useSelector(selectTemplates);
  const vulnsCount = vulnsSelected.length;
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedFields, setSelectedFields] = useState(null);
  const vulnsText = vulnsCount > 1 ? 'vulnerabilities' : 'vulnerability';
  const { themeStyles } = useContext(ThemeContext);

  useEffect(() => {
    dispatch(getTemplates());
  }, [dispatch]);

  const formatTemplates = (templatesList) => templatesList.map((template) => ({
    label: template.name,
    value: template
  }));

  const options = useMemo(() => formatTemplates(savedTemplates), [savedTemplates]);

  const onChange = (template) => {
    setSelectedTemplate(template);
    setSelectedFields(getFieldsPresence(template.value));
  };

  const toggleFn = (field) => (e) => {
    e.stopPropagation();
    setSelectedFields({ ...selectedFields, [field]: !selectedFields[field] });
  };

  const removeCustomAttribute = (customAttribute) => {
    setSelectedTemplate({
      ...selectedTemplate,
      value: {
        ...selectedTemplate.value,
        customfields: {
          ...selectedTemplate.value.customfields,
          [customAttribute]: null
        }
      }
    });
  };

  const close = () => dispatch(closeModal(MODAL_APPLY_TEMPLATE));

  const accept = () => {
    if (entity === 'vulns') dispatch(applyTemplate(selectedTemplate?.value, selectedFields));
    else if (entity === 'vulnsGeneral') dispatch(applyTemplateFromOutsideWs(selectedTemplate?.value, selectedFields));
    else dispatch(applyTemplateFromAsset(selectedTemplate?.value, selectedFields));
    close();
  };

  return (
    <ModalWrapper>
      <ModalContent>
        <Title>Select template from Vulnerability DB</Title>
        <Subtitle>Search for a template or select it from the list. </Subtitle>
        <Dropdown
          options={ options }
          onChange={ onChange }
          style={ inputDropdownStyle(themeStyles) }
          value={ selectedTemplate }
        />
        <Title>Preview template and choose fields</Title>
        {selectedTemplate?.value
          ? (
          <>
            <Subtitle>{ intl.formatMessage({ id: 'manage.applyTemplate.subtitle' }, { vulnsCount, vulnsText }) }</Subtitle>
            <TemplateField template={ selectedTemplate?.value } toggleFn={ toggleFn } selectedFields={ selectedFields } removeCustomAttribute={ removeCustomAttribute } />
          </>
            )
          : <Subtitle>Select a template above to load the preview.</Subtitle>}
        <ButtonsContainer>
          <CancelButton onClick={ close }>
            Cancel
          </CancelButton>
          <Button onClick={ accept } disabled={ !selectedTemplate?.value }>
            Apply
          </Button>
        </ButtonsContainer>
      </ModalContent>
    </ModalWrapper>
  );
};

export default ApplyTemplateModal;

const TemplateField = ({ template, toggleFn, selectedFields, removeCustomAttribute }) => {
  const {
    name, exploitation, desc, resolution, easeofresolution, policyviolations, impact, data, external_id, refs, customfields, cve, cvss
  } = getFieldsPresence(template);

  return (
    <Fields>
      { name ? <ExternalId expanded selected={ selectedFields.name } onChange={ toggleFn('name') } value={ template.name } title="Vulnerability Name" id="name" /> : null }
      { exploitation ? <Severity expanded selected={ selectedFields.exploitation } onChange={ toggleFn('exploitation') } value={ template.exploitation } title="Severity" id="severity" /> : null }
      { desc ? <Description expanded selected={ selectedFields.desc } onChange={ toggleFn('desc') } value={ template.desc } /> : null }
      { resolution ? <Resolution expanded selected={ selectedFields.resolution } onChange={ toggleFn('resolution') } value={ template.resolution } /> : null }
      { easeofresolution ? <ExternalId selected={ selectedFields.easeofresolution } onChange={ toggleFn('easeofresolution') } value={ template.easeofresolution } title="Ease of Resolution" id="ease_of_resolution" /> : null }
      { policyviolations ? <Policies selected={ selectedFields.policyviolations } onChange={ toggleFn('policyviolations') } value={ template.policyviolations } title="Policy Violations" id="policies" /> : null }
      { impact
        ? <ImpactsTab selected={ selectedFields.impact } onChange={ toggleFn('impact') } value={ template.impact } />
        : null }
      { data ? <Data selected={ selectedFields.data } onChange={ toggleFn('data') } value={ template.data } /> : null }
      { external_id ? <ExternalId selected={ selectedFields.external_id } onChange={ toggleFn('external_id') } value={ template.external_id } title="External ID" id="externalID" /> : null }
      { refs ? <Policies selected={ selectedFields.refs } onChange={ toggleFn('refs') } value={ template.refs } title="References" id="references" /> : null }
      { customfields ? <CustomAttributes expanded fields={ template.customfields } onChange={ (field) => removeCustomAttribute(field) } /> : null }
      { cve ? <CVE expanded value={ template.cve } selected={ selectedFields.cve } onChange={ toggleFn('cve') } /> : null }
      { cvss ? <ExternalId expanded selected={ selectedFields.cvss } onChange={ toggleFn('cvss') } value={ getValidCvss(template) } title="CVSS" id="cvss" /> : null }
    </Fields>
  );
};
